import * as React from 'react';
import { useState, createContext, useEffect } from 'react';
import _ from 'lodash';

import { AddToast, FormErrors } from '../../types/airframe';
import { TravelerProfileFormValues } from '../../types/travelerProfileForm';
import { SaveProfileValidationError } from '../../types/saveProfileValidationError';
import {
  TravelerProfile,
  ContactPoint,
} from '../../types/travelerProfile';
import {
  SearchOptInStateResponse,
  SendOptInStateResponse,
  Status,
  ValidateAndSearchResponse,
} from '../../service/smsOptInStateService';

import { TravelerProfileForm } from './TravelerProfileForm';
import {
  putTravelerProfile,
  sendTestAlert,
} from '../../service/travelerProfileService';
import { hasValidationErrors } from '../../lib/airframe/FormValidation';
import { transformToTravelerProfileUpdateRequest } from '../../transformer/travelerProfileTransformer';
import {
  ContactPointsAlertType,
  ContactPointsMessageType,
  contactPointFormIds,
} from './TravelerProfileConstants';
import {
  sendOptInSms,
  searchPhoneOptInState,
  validateAndSearch,
  notVerified,
} from '../../service/smsOptInStateService';
import { checkSearchResponseAndUpdate, checkOptInState } from './OptInState';

export type TravelerProfileFormHandlerProps = {
  accountId: string;
  isAgent: boolean;
  onSaveSuccess?: (values: TravelerProfileFormValues) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  travelerKey: string;
  travelerProfile: TravelerProfile;
  addToast: AddToast;
  render: (
    handleTestAlert: (addToast: AddToast, contactPoint: ContactPoint) => void,
    testAlertSent: boolean,
    sentContact: string | undefined,
    handleOnCloseTestAlertSent: () => void,
    handleSubmit: (
      errors: FormErrors,
      values: TravelerProfileFormValues,
      alertTypes: Array<keyof typeof ContactPointsAlertType>,
    ) => void,
    loading: boolean,
    hasFormError: boolean,
    testAlertError: Array<string>,
    saveProfilePhoneError: SaveProfileError,
    setSaveProfilePhoneError: (error: SaveProfileError) => void,
  ) => React.ReactElement<typeof TravelerProfileForm>;
};

export type SaveProfileError = {
  [key: string]: any;
};

export type TravelerProfileContextType = {
  doSendVerificationSms?: (
    phoneNumberE164: string,
    countryCode: string,
    resend: boolean,
  ) => Promise<SendOptInStateResponse>;
  doSearchPhoneVerificationStatus?: (
    phoneNumberE164: string,
    countryCode: string,
  ) => Promise<SearchOptInStateResponse>;
  doValidatePhoneAndSearchOptInStatus?: (
    phoneNumberE164: string,
    countryCode: string,
  ) => Promise<ValidateAndSearchResponse>;
  travelerProfile: TravelerProfile;
  setTravelerProfile?: (
    travelerProfile: TravelerProfile | ((travelerProfile: TravelerProfile) => TravelerProfile)
  ) => void;
  isAgent?: boolean;
  addToast?: AddToast;
};

export const TravelerProfileContext = createContext<TravelerProfileContextType>({
  travelerProfile: {} as TravelerProfile,
});

export const TravelerProfileFormHandler: React.FC<TravelerProfileFormHandlerProps> = ({
  accountId,
  isAgent,
  onSaveSuccess,
  loading,
  setLoading,
  travelerKey,
  travelerProfile,
  addToast,
  render,
}) => {
  const [testAlertSent, setTestAlertSent] = useState(false);
  const [sentContact, setSentContact] = useState<string | undefined>();
  const [hasFormError, setFormError] = useState(false);
  const [testAlertError, setTestAlertError] = useState<string[]>([]);
  const [saveProfilePhoneError, setSaveProfilePhoneError] = useState<SaveProfileError>({});

  const doSendVerificationSms = (
    phoneNumberE164: string,
    countryCode: string,
    resend: boolean,
  ): Promise<SendOptInStateResponse> => {
    const requestContactPoint = {
      travelerKey,
      phoneNumberE164,
      countryCode,
    };
    setLoading(true);
    return sendOptInSms(accountId, requestContactPoint, resend, isAgent).then(
      response => {
        setLoading(false);
        return response;
      },
    );
  };

  const doSearchPhoneVerificationStatus = (
    phoneNumberE164: string,
    countryCode: string,
  ): Promise<SearchOptInStateResponse> => {
    const requestContactPoint = {
      travelerKey,
      phoneNumberE164,
      countryCode,
    };
    setLoading(true);
    return searchPhoneOptInState(accountId, requestContactPoint, isAgent).then(
      response => {
        setLoading(false);
        return response;
      },
    );
  };

  const doValidatePhoneAndSearchOptInStatus = (
    phoneNumberE164: string,
    countryCode: string,
  ): Promise<ValidateAndSearchResponse> => {
    const requestContactPoint = {
      travelerKey,
      phoneNumberE164,
      countryCode,
    };
    setLoading(true);
    return validateAndSearch(requestContactPoint, isAgent).then(response => {
      setLoading(false);
      return response;
    });
  };

  const [augmentedTravelerProfile, setAugmentedTravelerProfile] = useState(travelerProfile);

  useEffect(() => {
    const newContactPoints: ContactPoint[] = [];
    augmentedTravelerProfile.contactPoints.forEach(contactPoint => {
      if (
        contactPoint.messageType === 'SMS' &&
        contactPoint.phoneNumberE164 &&
        contactPoint.countryCode
      ) {
        const setOptInState = (newOptInState: Status): void => {
          if (checkOptInState(newOptInState)) {
            setAugmentedTravelerProfile(localAugmentedTravelerProfile => {
              return {
                ...localAugmentedTravelerProfile,
                contactPoints: localAugmentedTravelerProfile.contactPoints.map(
                  localContactPoint => {
                    if (
                      localContactPoint.alertType === contactPoint.alertType
                    ) {
                      return {
                        ...localContactPoint,
                        optInState: newOptInState,
                        setOptInState,
                      };
                    } else {
                      return localContactPoint;
                    }
                  },
                ),
              };
            });
          }
        };

        const newState: ContactPoint = {
          ...contactPoint,
          setOptInState,
        };

        newContactPoints.push(newState);
      } else {
        newContactPoints.push(contactPoint);
      }
    });

    setAugmentedTravelerProfile(localAugmentedTravelerProfile => {
      return {
        ...localAugmentedTravelerProfile,
        contactPoints: newContactPoints,
      };
    });
  }, [
    ...augmentedTravelerProfile.contactPoints.map(contactPoint => {
      return contactPoint.messageType || 'null';
    }),
  ]);

  useEffect(() => {
    augmentedTravelerProfile.contactPoints.forEach(contactPoint => {
      if (contactPoint.messageType === 'SMS' && contactPoint.setOptInState) {
        if (
          contactPoint.phoneNumberE164 &&
          contactPoint.countryCode &&
          contactPoint.phoneNumberE164.length > 2
        ) {
          doSearchPhoneVerificationStatus(
            contactPoint.phoneNumberE164,
            contactPoint.countryCode,
          )
            .then(response => {
              if (contactPoint.setOptInState) {
                checkSearchResponseAndUpdate(
                  contactPoint.setOptInState,
                  addToast,
                  response,
                );
              }
            })
            .catch(err => {
              const message = 'Unable to search verification status.';
              addToast(message, {
                variant: 'error',
              });
              console.error(message, err);
            });
        } else {
          contactPoint.optInState = notVerified;
        }
      }
    });
  }, [
    ...augmentedTravelerProfile.contactPoints.map(contactPoint => {
      if (contactPoint) {
        const {
          messageType,
          phoneNumberE164,
          countryCode,
          setOptInState,
        } = contactPoint;
        return (
          (messageType || '') +
          (phoneNumberE164 || '') +
          (countryCode || '') +
          ((setOptInState && setOptInState.name) || '')
        );
      } else {
        return 'null';
      }
    }),
  ]);

  const TravelerProfileContextValue: TravelerProfileContextType = {
    doSendVerificationSms,
    doSearchPhoneVerificationStatus,
    doValidatePhoneAndSearchOptInStatus,
    travelerProfile: augmentedTravelerProfile,
    setTravelerProfile: setAugmentedTravelerProfile,
    isAgent,
    addToast,
  };

  const handleTestAlert = (addToast: AddToast, contactPoint: ContactPoint) => {
    setLoading(true);
    sendTestAlert(contactPoint)
      .then(sentContact => {
        setTestAlertSent(true);
        setLoading(false);
        setSentContact(sentContact);
      })
      .catch(error => {
        if (error.statusCode === 400 && !_.isEmpty(error.errors)) {
          const errorArr = error.errors.map((error: { message: string }) => error.message);
          setTestAlertError(errorArr);
        } else {
          addToast('Unable to send test alert, please try again later', {
            variant: 'error',
          });
        }
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOnCloseTestAlertSent = () => {
    setTestAlertSent(false);
    setLoading(false);
    setSentContact(undefined);
    setTestAlertError([]);
  };

  const cleanContactPointErrors = (
    errors: FormErrors,
    values: TravelerProfileFormValues,
    alertType: typeof ContactPointsAlertType[keyof typeof ContactPointsAlertType],
  ): FormErrors =>
    values[contactPointFormIds(alertType).messageType as keyof TravelerProfileFormValues] ===
    ContactPointsMessageType.SMS
      ? _.omit(errors, [contactPointFormIds(alertType).email])
      : _.omit(errors, [
        contactPointFormIds(alertType).phone,
        contactPointFormIds(alertType).phoneInternational,
      ]);

  const cleanFormErrors = (
    errors: FormErrors,
    values: TravelerProfileFormValues,
  ): FormErrors => {
    let cleanedErrors = cleanContactPointErrors(
      errors,
      values,
      ContactPointsAlertType.PRE_TRIP,
    );
    cleanedErrors = cleanContactPointErrors(
      cleanedErrors,
      values,
      ContactPointsAlertType.TRAVELING,
    );
    return cleanedErrors;
  };

  const handleSubmit = (
    errors: FormErrors,
    values: TravelerProfileFormValues,
    alertTypes: Array<keyof typeof ContactPointsAlertType>,
  ) => {
    const cleanedErrors = cleanFormErrors(errors, values);

    if (!hasValidationErrors(cleanedErrors)) {
      setLoading(true);
      setFormError(false);

      Object.keys(values)
        .forEach(item => {
          if (
            /^.*MessageType$/.test(item) &&
            typeof values[item as keyof TravelerProfileFormValues] === 'object' &&
            (values[item as keyof TravelerProfileFormValues] as any).value === 'SMS'
          ) {
            const SMSAlertType = item.replace('MessageType', '');
            const { countryCode, number } = (values as any)[
              SMSAlertType + 'PhoneInternational'
            ];

            doSendVerificationSms(number, countryCode, false)
              .then(result => {
                const contactPoint = augmentedTravelerProfile.contactPoints.find(
                  contactPoint => {
                    return contactPoint.alertType === SMSAlertType;
                  },
                );
                if (
                  result &&
                  result.search &&
                  contactPoint &&
                  contactPoint.setOptInState
                ) {
                  checkSearchResponseAndUpdate(                
                    contactPoint.setOptInState,
                    addToast,
                    result.search,
                  );
                }
              })
              .catch(err => {
                addToast('Error attempting to update alert optInStates', {
                  variant: 'error',
                });
                console.error(err);
              });
          }
        });

      putTravelerProfile(
        transformToTravelerProfileUpdateRequest(values),
        travelerKey,
        isAgent,
      )
        .then(() => {
          addToast('Save successful');
          onSaveSuccess && onSaveSuccess(values);
        })
        .catch(error => {
          setFormError(true);
          if (
            error.statusCode === 400 &&
            error.errorBody &&
            error.errorBody.contactPointErrors
          ) {
            mapContactPointFieldErrors(error.errorBody, errors, alertTypes);
          } else {
            addToast('Unable to save', {
              variant: 'error',
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setFormError(true);
    }
  };

  const mapContactPointFieldErrors = (
    error: SaveProfileValidationError,
    formErrors: FormErrors,
    alertTypes: Array<keyof typeof ContactPointsAlertType>,
  ) => {
    error.contactPointErrors.forEach((contactPointError, index) => {
      const fieldName = alertTypes[index] + 'PhoneInternational';
      if (!_.isEmpty(contactPointError.errors)) {
        const message = contactPointError.errors
          .map(error => error.message)
          .join('; ');
        formErrors.updateFieldErrorText(fieldName, message);
        saveProfilePhoneError[fieldName] = message;
        setSaveProfilePhoneError(saveProfilePhoneError);
      }
    });
  };

  return (
    <TravelerProfileContext.Provider value={TravelerProfileContextValue}>
      {TravelerProfileContextValue &&
        render(
          handleTestAlert,
          testAlertSent,
          sentContact,
          handleOnCloseTestAlertSent,
          handleSubmit,
          loading,
          hasFormError,
          testAlertError,
          saveProfilePhoneError,
          setSaveProfilePhoneError,
        )}
    </TravelerProfileContext.Provider>
  );
};